/*
 * Song search personalizer — markup in snippets/ti-song-personalizer.liquid.
 *
 * Flow (Claude Design "SongPersonalizer"): search Genius through the lyrics API →
 * tap a result → we fetch and preview the lyrics → "Use this song". Fallbacks:
 * "Enter your own lyrics" and "Ask our team". Up to 3 songs per item; they print
 * one after another as one all-over print.
 *
 * The songs are written to hidden line-item property inputs inside the product
 * form. The Song Title input carries data-ti-required, so the product section's
 * ATC gating picks it up from the `input` event we dispatch.
 *
 * Lyrics API (tuneink-lyrics-api):
 *   GET {api}/api/search?mode=all&q=…  → { results: [{ id, title, artist, image, release, url, match[], snippet, noise }] }
 *   GET {api}/api/lyrics?url=…         → { lyrics }
 *   POST {api}/api/print-file          → { id, path }   the print file (JSON) for this item
 *   GET {api}{path}                    → { print_text, songs: [...], note_for_team }
 *
 * The lyrics never go on the order (too long for OrderDesk): the songs are saved
 * as one print file and the order carries only its URL (_Lyrics File).
 *
 * Edit from the cart: the cart drawer's "Edit songs" opens this page with
 * ?edit=<line key>. The line's variant, quantity and songs (from its print file)
 * are loaded, the button reads "Save changes", and saving rewrites that cart line.
 */
(function () {
  'use strict';

  // UI strings come from the theme locale (snippets/ti-js-strings.liquid -> window.tiStrings);
  // the English text here is the fallback.
  var STR = window.tiStrings || {};
  function tr(key, fallback) { return STR[key] != null ? STR[key] : fallback; }

  var MAX_SONGS = 3;
  var TONES = ['#1E3A5F', '#3D5A80', '#525E6C', '#2D4A6B'];
  var SEARCH_TIMEOUT = 15000;
  var LYRICS_TIMEOUT = 25000;
  var SAVE_TIMEOUT = 15000;
  var MIN_WORDS = 20;
  var NOTE_MAX = 500;
  var UNDO_MS = 5000;
  var TITLES = {
    idle: tr('sp_find', 'Find your song'), loading: tr('sp_find', 'Find your song'), results: tr('sp_find', 'Find your song'), noresults: tr('sp_find', 'Find your song'), error: tr('sp_find', 'Find your song'),
    confirm: tr('sp_confirm', 'Confirm your song'), lyricsError: tr('sp_confirm', 'Confirm your song'), manual: tr('sp_manual', 'Enter your own lyrics'), assist: tr('sp_assist', 'Ask our team')
  };
  var SEARCH_VIEWS = ['idle', 'loading', 'results', 'noresults', 'error'];
  var SOURCE_VALUE = { search: 'Search', manual: 'Customer', assist: 'Team to find' };
  var FILE_SOURCE = { search: 'search', manual: 'customer', assist: 'team' };
  var FROM_FILE_SOURCE = { search: 'search', customer: 'manual', team: 'assist' };

  var I_NOTE = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M9 18V5l11-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="17" cy="16" r="3"/></svg>';
  var I_SEARCH = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="21" y2="21"/></svg>';
  var I_CHECK = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1E3A5F" stroke-width="1.8" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>';
  var I_TICK = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>';
  var I_CHAT = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1E3A5F" stroke-width="1.8" aria-hidden="true"><path d="M21 12a8 8 0 0 1-11.6 7.1L4 21l1.9-5.4A8 8 0 1 1 21 12z"/></svg>';
  var I_INFO = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><circle cx="12" cy="12" r="9"/><line x1="12" y1="11" x2="12" y2="16"/><circle cx="12" cy="8" r=".6" fill="currentColor"/></svg>';
  var I_PLUS = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';
  var I_PENCIL = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" aria-hidden="true"><path d="M4 20h4L19 9l-4-4L4 16v4z"/><path d="M13.5 6.5l4 4"/></svg>';
  var I_GIFT = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><rect x="3" y="8" width="18" height="4"/><path d="M5 12v9h14v-9M12 8v13M12 8c-2-4-6-4-6-1.5S10 8 12 8zM12 8c2-4 6-4 6-1.5S14 8 12 8z"/></svg>';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Highlight whole words of the query only (never a lone letter inside a word).
  function hl(text, q) {
    text = String(text || '');
    var words = String(q || '').toLowerCase().split(/[^\p{L}\p{N}']+/u).filter(Boolean);
    if (!words.length) return esc(text);
    var alt = words.map(function (w) { return w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }).join('|');
    var re = new RegExp("(^|[^\\p{L}\\p{N}'])(" + alt + ")(?=$|[^\\p{L}\\p{N}'])", 'giu');
    var out = '', last = 0, m;
    while ((m = re.exec(text))) {
      var start = m.index + m[1].length;
      out += esc(text.slice(last, start)) + '<mark>' + esc(m[2]) + '</mark>';
      last = start + m[2].length;
      re.lastIndex = last;
    }
    return out + esc(text.slice(last));
  }

  // What gets printed: section labels ([Chorus], [Verse 2: Artist]) removed, one blank line between stanzas.
  function cleanLyrics(raw) {
    return String(raw || '')
      .split('\n')
      .filter(function (l) { return !/^\s*\[[^\]]*\]\s*$/.test(l); })
      .map(function (l) { return l.replace(/\s+$/, ''); })
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  function wordCount(s) { s = String(s || '').trim(); return s ? s.split(/\s+/).length : 0; }

  function hasArt(url) { return !!url && !/default_cover|default_avatar/.test(url); }

  function artHtml(song, size, i, num) {
    var inner;
    if (song && song.source === 'search' && hasArt(song.image)) {
      inner = '<span class="ti-sp-art ti-sp-art--' + size + '"><img src="' + esc(song.image) + '" alt="" loading="lazy" width="' + size + '" height="' + size + '"></span>';
    } else {
      var tone = TONES[Math.abs(Number(i) || 0) % TONES.length];
      inner = '<span class="ti-sp-art ti-sp-art--' + size + '" style="background:' + tone + '">' + I_NOTE + '</span>';
    }
    if (!num) return inner;
    return '<span class="ti-sp-art-wrap">' + inner + '<span class="ti-sp-num" aria-hidden="true">' + num + '</span></span>';
  }

  function withTimeout(promise, ms) {
    return new Promise(function (resolve, reject) {
      var timer = setTimeout(function () { reject(new Error('timeout')); }, ms);
      promise.then(function (v) { clearTimeout(timer); resolve(v); }, function (e) { clearTimeout(timer); reject(e); });
    });
  }

  function getJSON(url, timeoutMs) {
    return withTimeout(fetch(url, { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (d) {
        if (!r.ok) throw new Error(d.error || 'HTTP ' + r.status);
        return d;
      });
    }), timeoutMs);
  }

  // Cart writes for edit mode go through XHR on purpose: assets/cart-b2g1.js wraps
  // window.fetch and would treat the re-added line as a new add (and maybe claim
  // it as a free gift). The item count doesn't change when editing.
  function cartPost(path, data) {
    return new Promise(function (resolve, reject) {
      var x = new XMLHttpRequest();
      x.open('POST', ((window.routes && window.routes.root) || '/').replace(/\/?$/, '/') + path.replace(/^\//, ''));
      x.setRequestHeader('Content-Type', 'application/json');
      x.setRequestHeader('Accept', 'application/json');
      x.onload = function () {
        var d = null;
        try { d = JSON.parse(x.responseText); } catch (e) { /* not JSON */ }
        if (x.status >= 200 && x.status < 300 && d && !d.status) resolve(d);
        else reject(new Error((d && (d.description || d.message)) || 'HTTP ' + x.status));
      };
      x.onerror = function () { reject(new Error('network')); };
      x.send(JSON.stringify(data));
    });
  }

  function titleCase(s) { return String(s || '').replace(/(^|\s)(\p{L})/gu, function (m, a, b) { return a + b.toUpperCase(); }); }

  // `_Songs` (read by the cart drawer): "source|title|artist" per song, joined by "§".
  function songsProp(songs) {
    function part(v) { return String(v || '').replace(/[|§\n\r]+/g, ' ').trim(); }
    return songs.map(function (s) { return [FILE_SOURCE[s.source], part(s.title), part(s.artist)].join('|'); }).join('§');
  }

  function init(box) {
    if (box.__tiSp) return;
    box.__tiSp = true;

    var api = (box.getAttribute('data-api') || '').trim().replace(/\/+$/, '');
    var productId = box.getAttribute('data-product-id') || '';
    var pdp = box.closest('[id^="ti-pdp-"]') || document;
    var form = box.closest('form');
    var fields = {};
    box.querySelectorAll('[data-ti-sp-field]').forEach(function (el) { fields[el.getAttribute('data-ti-sp-field')] = el; });
    var notesEl = box.querySelector('[data-ti-sp-notes]');
    var emptyEl = box.querySelector('[data-ti-sp-empty]');
    var selectedEl = box.querySelector('[data-ti-sp-selected]');

    var st = {
      view: null, query: '', results: [], fetching: -1, dupe: -1, song: null, songIndex: 0, lyrics: '',
      songs: [], file: '', slot: 0, mode: 'add', prefill: null, edit: null
    };
    var searchSeq = 0, lyricsSeq = 0, fileSeq = 0, debounce = null, lastFocus = null, toastTimer = null, removed = null;
    var lyricsCache = {};

    /* ------------------------------------------------------------ panel shell */
    var panel = document.createElement('div');
    panel.className = 'ti-sp-panel';
    panel.hidden = true;
    panel.innerHTML =
      '<div class="ti-sp-scrim" data-act="close"></div>' +
      '<div class="ti-sp-dialog" role="dialog" aria-modal="true" aria-labelledby="ti-sp-title" tabindex="-1">' +
        '<div class="ti-sp-grab"><span></span></div>' +
        '<div class="ti-sp-head">' +
          '<button type="button" class="ti-sp-cancel" data-act="close">' + tr('sp_cancel', 'Cancel') + '</button>' +
          '<div class="ti-sp-headmid"><div class="ti-sp-slot" hidden></div><h2 class="ti-sp-title" id="ti-sp-title"></h2></div>' +
          '<button type="button" class="ti-sp-x" data-act="close" aria-label="' + tr('sp_close', 'Close') + '">&times;</button>' +
        '</div>' +
        '<div class="ti-sp-fieldwrap">' +
          '<div class="ti-sp-field">' + I_SEARCH +
            '<input type="text" inputmode="search" enterkeyhint="search" autocomplete="off" autocorrect="off" spellcheck="false" placeholder="' + tr('sp_search_ph', 'Search your song or a lyric line…') + '" aria-label="' + tr('sp_search_label', 'Search songs') + '">' +
            '<button type="button" class="ti-sp-clear" data-act="clear" aria-label="' + tr('sp_clear', 'Clear search') + '" hidden>&times;</button>' +
          '</div>' +
        '</div>' +
        '<div class="ti-sp-body" aria-live="polite"></div>' +
      '</div>';
    document.body.appendChild(panel);

    var toast = document.createElement('div');
    toast.className = 'ti-sp-toast';
    toast.setAttribute('role', 'status');
    toast.hidden = true;
    document.body.appendChild(toast);

    var dialog = panel.querySelector('.ti-sp-dialog');
    var titleEl = panel.querySelector('.ti-sp-title');
    var slotEl = panel.querySelector('.ti-sp-slot');
    var fieldWrap = panel.querySelector('.ti-sp-fieldwrap');
    var input = panel.querySelector('input');
    var clearBtn = panel.querySelector('.ti-sp-clear');
    var body = panel.querySelector('.ti-sp-body');

    /* ---------------------------------------------------------- panel views */
    function addingLater() { return st.mode === 'add' && st.slot >= 1; }

    function skeletons() {
      var s = '<div class="ti-sp-skel"><i></i><div><i></i><i></i><i></i></div></div>';
      return '<div class="ti-sp-count">' + tr('sp_searching', 'Searching…') + '</div><div class="ti-sp-list">' + s + s + s + '</div>';
    }

    function stillStuck() {
      return '<div><button type="button" class="ti-sp-link" data-act="assist">' + tr('sp_still_stuck', 'Still stuck? Ask our team to find it') + '</button></div>';
    }

    function resultCard(r, i) {
      var q = st.query;
      var badges = '';
      if ((r.match || []).indexOf('title') > -1) badges += '<span class="ti-sp-badge">' + tr('sp_match_title', 'Title match') + '</span>';
      if ((r.match || []).indexOf('artist') > -1) badges += '<span class="ti-sp-badge">' + tr('sp_match_artist', 'Artist match') + '</span>';
      if ((r.match || []).indexOf('lyrics') > -1) badges += '<span class="ti-sp-badge">' + tr('sp_match_lyrics', 'Lyrics match') + '</span>';
      var lines = String(r.snippet || '').split('\n').map(function (l) { return l.trim(); }).filter(Boolean).slice(0, 4);
      var loading = st.fetching === i;
      return '<button type="button" class="ti-sp-card" data-act="pick" data-i="' + i + '" aria-pressed="' + loading + '">' +
        artHtml({ source: 'search', image: r.image }, 56, i) +
        '<span class="ti-sp-card__body">' +
          '<span class="ti-sp-card__title">' + hl(r.title, q) + '</span>' +
          '<span class="ti-sp-card__artist">' + hl(r.artist, q) + '</span>' +
          (r.release ? '<span class="ti-sp-card__date">' + esc(r.release) + '</span>' : '') +
          (badges ? '<span class="ti-sp-badges">' + badges + '</span>' : '') +
          (lines.length ? '<span class="ti-sp-snippet">' + lines.map(function (l) { return '<span>' + hl(l, q) + '</span>'; }).join('') + '</span>' : '') +
          (st.dupe === i ? '<span class="ti-sp-dupe" role="status">' + I_TICK + tr('sp_dupe', 'Already on your shirt') + '</span>' : '') +
          (loading ? '<span class="ti-sp-getting"><span class="ti-sp-spin"></span><span>' + tr('sp_getting', 'Getting lyrics…') + '</span></span>' : '') +
        '</span>' +
      '</button>';
    }

    function lyricsHtml(raw) {
      return String(raw || '').split('\n').map(function (l) {
        if (/^\s*\[[^\]]*\]\s*$/.test(l)) return '<div class="ti-sp-label">' + esc(l.trim().slice(1, -1)) + '</div>';
        if (!l.trim()) return '<div class="ti-sp-gap"></div>';
        return '<div>' + esc(l) + '</div>';
      }).join('');
    }

    function manualForm(p) {
      p = p || {};
      return '<form class="ti-sp-form" data-form="manual" novalidate>' +
        '<p class="ti-sp-muted">' + tr('sp_manual_intro', 'Great for original songs, poems, vows or anything we can’t find.') + '</p>' +
        '<label><span class="lbl">' + tr('sp_song_title', 'Song title') + ' <span class="req">*</span></span>' +
          '<input name="title" value="' + esc(p.title) + '" placeholder="' + tr('sp_title_ph_manual', 'e.g. Our First Dance') + '" maxlength="200">' +
          '<span class="err" data-err="title" hidden>' + tr('sp_err_title', 'Add a song title.') + '</span></label>' +
        '<label><span class="lbl">' + tr('sp_artist', 'Artist') + ' <em>(' + tr('sp_optional', 'optional') + ')</em></span>' +
          '<input name="artist" value="' + esc(p.artist) + '" placeholder="' + tr('sp_artist_ph_manual', 'Leave blank for an original song') + '" maxlength="200"></label>' +
        '<label><span class="lbl">' + tr('sp_lyrics', 'Lyrics') + ' <span class="req">*</span></span>' +
          '<textarea name="lyrics" rows="10" placeholder="' + tr('sp_lyrics_ph', 'Paste or type your lyrics. Put each line on a new line.') + '">' + esc(p.lyrics) + '</textarea>' +
          '<span class="meta"><span class="err" data-err="lyrics"></span><span data-counter="lyrics"></span></span></label>' +
        '<div class="ti-sp-form-actions">' +
          '<button type="submit" class="ti-sp-btn">' + tr('sp_use_lyrics', 'Use these lyrics') + '</button>' +
          '<button type="button" class="ti-sp-btn ti-sp-btn--ghost" data-act="search">' + tr('sp_back_search', 'Back to search') + '</button>' +
        '</div>' +
      '</form>';
    }

    function assistForm(p) {
      p = p || {};
      return '<form class="ti-sp-form" data-form="assist" novalidate>' +
        '<div class="ti-sp-callout">' + I_CHAT + '<p>' + tr('sp_assist_intro', 'Tell us what you’re looking for and our team will find the lyrics and print your order. We’ll only contact you if we need more details.') + '</p></div>' +
        '<label><span class="lbl">' + tr('sp_song_title', 'Song title') + ' <span class="req">*</span></span>' +
          '<input name="title" value="' + esc(p.title) + '" placeholder="' + tr('sp_title_ph_assist', 'e.g. Amazing Grace') + '" maxlength="200">' +
          '<span class="err" data-err="title" hidden>' + tr('sp_err_title_assist', 'Add the song title, even if you’re not sure it’s exact.') + '</span></label>' +
        '<label><span class="lbl">' + tr('sp_artist', 'Artist') + ' <em>(' + tr('sp_if_known', 'if you know it') + ')</em></span>' +
          '<input name="artist" value="' + esc(p.artist) + '" placeholder="' + tr('sp_artist_ph_assist', 'e.g. Our church choir') + '" maxlength="200"></label>' +
        '<label><span class="lbl">' + tr('sp_msg_label', 'Message for our team') + ' <span class="req">*</span></span>' +
          '<textarea name="msg" rows="5" maxlength="' + NOTE_MAX + '" placeholder="' + tr('sp_msg_ph', 'Which version, a line you remember, a link to the song, or anything else that helps.') + '">' + esc(p.msg) + '</textarea>' +
          '<span class="meta"><span class="err" data-err="msg"></span><span data-counter="msg"></span></span></label>' +
        '<div class="ti-sp-form-actions">' +
          '<button type="submit" class="ti-sp-btn">' + tr('sp_send_team', 'Send to our team') + '</button>' +
          '<button type="button" class="ti-sp-btn ti-sp-btn--ghost" data-act="search">' + tr('sp_back_search', 'Back to search') + '</button>' +
        '</div>' +
      '</form>';
    }

    function render() {
      var v = st.view;
      titleEl.textContent = TITLES[v] || '';
      // "Song 2 of 3" once there is (or will be) more than one song.
      var showSlot = st.slot >= 1 || st.songs.length >= 2;
      slotEl.hidden = !showSlot;
      slotEl.textContent = tr('sp_slot', 'Song {n} of {max}').replace('{n}', st.slot + 1).replace('{max}', MAX_SONGS);
      dialog.setAttribute('aria-label', (showSlot ? slotEl.textContent + ', ' : '') + (TITLES[v] || ''));
      fieldWrap.hidden = SEARCH_VIEWS.indexOf(v) === -1;
      clearBtn.hidden = !input.value;
      var h = '';
      var song = st.song || {};

      if (v === 'idle') {
        h = '<p class="ti-sp-muted">' + tr('sp_idle', 'Type a song title, an artist, or any line you remember. We’ll search as you type.') + '</p>';
      } else if (v === 'loading') {
        h = skeletons();
      } else if (v === 'results') {
        var n = st.results.length;
        h = '<div class="ti-sp-count">' + (n === 1 ? tr('sp_results_one', '{n} result – tap a song to use it') : tr('sp_results_other', '{n} results – tap a song to use it')).replace('{n}', n) + '</div>' +
          '<div class="ti-sp-list">' + st.results.map(resultCard).join('') + '</div>' +
          '<div class="ti-sp-more">' + tr('sp_not_right', 'Not the right one?') + ' <button type="button" class="ti-sp-link" data-act="manual">' + tr('sp_manual', 'Enter your own lyrics') + '</button></div>' +
          '<div class="ti-sp-more" style="margin-top:0">' + tr('sp_cant_find', 'Can’t find it at all?') + ' <button type="button" class="ti-sp-link" data-act="assist">' + tr('sp_assist', 'Ask our team') + '</button></div>';
      } else if (v === 'noresults') {
        h = '<div class="ti-sp-state"><span class="ti-sp-state__icon">' + I_SEARCH + '</span>' +
          '<div class="ti-sp-state__title">' + tr('sp_nores_title', 'We couldn’t find that song.') + '</div>' +
          '<p>' + tr('sp_nores_body', 'Check the spelling, or try the artist’s name or a line from the lyrics.') + '</p>' +
          '<div class="ti-sp-actions"><button type="button" class="ti-sp-btn ti-sp-btn--ghost" data-act="search">' + tr('sp_try_another', 'Try another search') + '</button>' +
          '<button type="button" class="ti-sp-btn" data-act="manual">' + tr('sp_manual', 'Enter your own lyrics') + '</button></div>' + stillStuck() + '</div>';
      } else if (v === 'error') {
        h = '<div class="ti-sp-state"><span class="ti-sp-state__icon ti-sp-state__icon--warn">!</span>' +
          '<div class="ti-sp-state__title">' + tr('sp_err_title_state', 'Song search isn’t working right now.') + '</div>' +
          '<p>' + tr('sp_err_body', 'You can still personalize your item. Type the song title and lyrics yourself and we’ll print them just the same.') + '</p>' +
          '<div class="ti-sp-actions"><button type="button" class="ti-sp-btn" data-act="manual">' + tr('sp_manual', 'Enter your own lyrics') + '</button>' +
          '<button type="button" class="ti-sp-link" data-act="retry">' + tr('sp_retry', 'Try searching again') + '</button></div>' + stillStuck() + '</div>';
      } else if (v === 'confirm') {
        h = '<div class="ti-sp-confirm-head">' + artHtml({ source: 'search', image: song.image }, 64, st.songIndex) + '<div style="min-width:0">' +
            '<div class="ti-sp-confirm-head__title">' + esc(song.title) + '</div>' +
            '<div class="ti-sp-confirm-head__artist">' + esc(song.artist) + '</div>' +
            (song.release ? '<div class="ti-sp-confirm-head__date">' + esc(song.release) + '</div>' : '') +
          '</div></div>' +
          '<div class="ti-sp-eyebrow" style="margin-top:20px;margin-bottom:8px">' + tr('sp_preview', 'Lyrics preview') + '</div>' +
          '<div class="ti-sp-lyrics" tabindex="0" aria-label="' + tr('sp_preview', 'Lyrics preview') + '">' + lyricsHtml(st.lyrics) + '</div>' +
          '<div class="ti-sp-note">' + I_CHECK + '<span>' + tr('sp_print_note', 'These lyrics will be printed all over your shirt. Labels like Chorus aren’t printed.') + '</span></div>' +
          '<div class="ti-sp-actions" style="margin-top:18px"><button type="button" class="ti-sp-btn ti-sp-btn--lg" data-act="use">' + (addingLater() ? tr('sp_add_song', 'Add this song') : tr('sp_use_song', 'Use this song')) + '</button>' +
          (st.results.length ? '<button type="button" class="ti-sp-btn ti-sp-btn--ghost ti-sp-btn--lg" data-act="back">' + tr('sp_back_results', 'Back to results') + '</button>' : '<button type="button" class="ti-sp-btn ti-sp-btn--ghost ti-sp-btn--lg" data-act="search">' + tr('sp_search_another', 'Search another song') + '</button>') + '</div>' +
          '<div style="text-align:center;margin-top:12px"><button type="button" class="ti-sp-link ti-sp-link--muted" data-act="fix">' + tr('sp_fix', 'Lyrics look wrong? Enter them yourself') + '</button></div>';
      } else if (v === 'lyricsError') {
        h = '<div class="ti-sp-state"><span class="ti-sp-state__icon ti-sp-state__icon--warn">!</span>' +
          '<div class="ti-sp-state__title">' + tr('sp_lyrerr_title', 'We couldn’t load the lyrics.') + '</div>' +
          '<p>' + tr('sp_lyrerr_body', 'The lyrics for “{title}” by {artist} aren’t available. Pick another version, or type them in yourself.').replace('{title}', esc(song.title)).replace('{artist}', esc(song.artist)) + '</p>' +
          '<div class="ti-sp-actions"><button type="button" class="ti-sp-btn" data-act="back">' + tr('sp_choose_another', 'Choose another song') + '</button>' +
          '<button type="button" class="ti-sp-btn ti-sp-btn--ghost" data-act="fix">' + tr('sp_manual', 'Enter your own lyrics') + '</button></div>' + stillStuck() + '</div>';
      } else if (v === 'manual') {
        h = manualForm(st.prefill);
      } else if (v === 'assist') {
        h = assistForm(st.prefill);
      }
      body.innerHTML = h;
      updateCounters();
    }

    function setView(v) {
      st.view = v;
      render();
      if (SEARCH_VIEWS.indexOf(v) === -1) body.scrollTop = 0;
    }

    function updateCounters() {
      var ly = body.querySelector('[data-counter="lyrics"]');
      if (ly) {
        var val = body.querySelector('textarea[name="lyrics"]').value;
        var lines = val.split('\n').filter(function (l) { return l.trim(); }).length;
        ly.textContent = (lines === 1 ? tr('sp_lines_one', '{n} line') : tr('sp_lines_other', '{n} lines')).replace('{n}', lines) + ' · ' + tr('sp_chars', '{n} chars').replace('{n}', val.length);
      }
      var msg = body.querySelector('[data-counter="msg"]');
      if (msg) msg.textContent = body.querySelector('textarea[name="msg"]').value.length + ' / ' + NOTE_MAX;
    }

    /* --------------------------------------------------------------- search */
    function runSearch(q) {
      clearTimeout(debounce);
      q = String(q || '').trim();
      st.query = q;
      st.fetching = -1;
      st.dupe = -1;
      var my = ++searchSeq;
      lyricsSeq++;
      if (q.length < 3) { setView('idle'); return; }
      if (!api) { setView('error'); return; }
      setView('loading');
      getJSON(api + '/api/search?mode=all&q=' + encodeURIComponent(q), SEARCH_TIMEOUT).then(function (d) {
        if (my !== searchSeq) return;
        st.results = (d.results || []).filter(function (r) { return !r.noise && r.url; }).slice(0, 20);
        setView(st.results.length ? 'results' : 'noresults');
      }).catch(function () {
        if (my !== searchSeq) return;
        setView('error');
      });
    }

    // Same Genius song already in the list (ignoring the row being changed).
    function inList(r) {
      return st.songs.some(function (s, i) {
        return s.source === 'search' && s.url === r.url && !(st.mode === 'change' && i === st.slot);
      });
    }

    function pick(i) {
      if (st.fetching >= 0) return;
      var r = st.results[i];
      if (!r) return;
      if (inList(r)) { st.dupe = i; render(); return; }
      st.dupe = -1;
      st.fetching = i;
      st.song = r;
      st.songIndex = i;
      render();
      var my = ++lyricsSeq;
      var cached = lyricsCache[r.url];
      var job = cached ? Promise.resolve({ lyrics: cached }) : getJSON(api + '/api/lyrics?url=' + encodeURIComponent(r.url), LYRICS_TIMEOUT);
      job.then(function (d) {
        if (my !== lyricsSeq) return;
        st.fetching = -1;
        if (!d.lyrics || wordCount(cleanLyrics(d.lyrics)) < 1) throw new Error('empty');
        lyricsCache[r.url] = d.lyrics;
        st.lyrics = d.lyrics;
        setView('confirm');
      }).catch(function () {
        if (my !== lyricsSeq) return;
        st.fetching = -1;
        st.lyrics = '';
        setView('lyricsError');
      });
    }

    /* ------------------------------------------------------ print file / fields */
    function makeFile(songs) {
      var payload = JSON.stringify({
        songs: songs.map(function (s) {
          return {
            title: s.title, artist: s.artist || '', source: FILE_SOURCE[s.source], genius_url: s.url || '',
            lyrics: s.lyrics || '', note: s.note || '', image: s.source === 'search' ? (s.image || '') : '', release: s.release || ''
          };
        })
      });
      function post() {
        return withTimeout(fetch(api + '/api/print-file', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload })
          .then(function (r) { return r.json().then(function (d) { if (!r.ok || !d.path) throw new Error(d.error || 'HTTP ' + r.status); return api + d.path; }); }), SAVE_TIMEOUT);
      }
      if (!api) return Promise.reject(new Error('no api'));
      return post().catch(function () { return post(); });
    }

    function setField(name, value) {
      var el = fields[name];
      if (!el) return;
      el.value = value || '';
      // Empty properties are left out of the cart line entirely.
      if (name !== 'title') el.disabled = !value;
    }

    // Writes the current songs to the line-item property inputs. Song Title (the
    // required field) stays empty until the print file exists, so Add to cart can
    // never go through without the lyrics on file.
    function applyFields() {
      var songs = st.songs;
      var multi = songs.length > 1;
      setField('title', songs.length && st.file ? songs.map(function (s) { return s.title; }).join(' / ') : '');
      setField('artist', songs.map(function (s) { return s.artist; }).filter(Boolean).join(' / '));
      setField('file', songs.length ? st.file : '');
      setField('source', songs.map(function (s) { return SOURCE_VALUE[s.source]; }).join(' / '));
      setField('songs', songs.length ? songsProp(songs) : '');
      if (notesEl) {
        notesEl.innerHTML = songs.map(function (s, i) {
          if (s.source !== 'assist' || !s.note) return '';
          var name = multi ? 'Note for our team (song ' + (i + 1) + ')' : 'Note for our team';
          return '<input type="hidden" name="properties[' + esc(name) + ']" value="' + esc(s.note) + '">';
        }).join('');
      }
      fields.title.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Saved per product so a refresh keeps the customer's songs (14 days).
    // Never while editing a cart line: that would overwrite their in-progress choice.
    var storeKey = 'ti-sp:v2:' + (productId || location.pathname);
    var oldKey = 'ti-sp:v1:' + (productId || location.pathname);
    var STORE_DAYS = 14;

    function persist() {
      if (st.edit) return;
      try {
        if (st.songs.length) localStorage.setItem(storeKey, JSON.stringify({ at: Date.now(), songs: st.songs, file: st.file }));
        else localStorage.removeItem(storeKey);
        localStorage.removeItem(oldKey);
      } catch (e) { /* private mode / storage full: the choice just isn't remembered */ }
    }

    function loadSaved() {
      function fresh(d) { return d && Date.now() - d.at < STORE_DAYS * 864e5; }
      try {
        var d = JSON.parse(localStorage.getItem(storeKey) || 'null');
        if (fresh(d) && d.songs && d.songs.length) return { songs: d.songs.slice(0, MAX_SONGS), file: d.file || '' };
        // Saved by the one-song version.
        var o = JSON.parse(localStorage.getItem(oldKey) || 'null');
        if (fresh(o) && o.song && o.song.title) return { songs: [o.song], file: o.song.file || '' };
      } catch (e) { /* ignore */ }
      return null;
    }

    // Re-creates the print file for the current list (after a remove / undo / restore).
    function syncFile() {
      var my = ++fileSeq;
      st.file = '';
      boxError(false);
      applyFields();
      persist();
      if (!st.songs.length) return Promise.resolve();
      return makeFile(st.songs).then(function (url) {
        if (my !== fileSeq) return;
        st.file = url;
        applyFields();
        persist();
      }).catch(function () {
        if (my === fileSeq) boxError(true);
      });
    }

    // Adds or replaces the song in the slot being edited, once its file is saved.
    function commit(c, btn) {
      var err = body.querySelector('.ti-sp-save-err');
      if (err) err.remove();
      var songs = st.songs.slice();
      if (st.mode === 'change' && st.slot < songs.length) songs[st.slot] = c;
      else if (songs.length < MAX_SONGS) songs.push(c);
      var label = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = tr('sp_saving', 'Saving…'); }
      var my = ++fileSeq;
      makeFile(songs).then(function (url) {
        if (my !== fileSeq) return;
        st.songs = songs;
        st.file = url;
        boxError(false);
        applyFields();
        renderBox();
        persist();
        close();
      }).catch(function () {
        if (!btn || !document.contains(btn)) return;
        btn.disabled = false;
        btn.textContent = label;
        var p = document.createElement('p');
        p.className = 'ti-sp-save-err';
        p.setAttribute('role', 'alert');
        p.textContent = tr('sp_save_err', 'We couldn’t save your lyrics. Please try again.');
        btn.parentNode.parentNode.insertBefore(p, btn.parentNode);
      });
    }

    function removeAt(i) {
      var gone = st.songs[i];
      if (!gone) return;
      st.songs = st.songs.slice(0, i).concat(st.songs.slice(i + 1));
      removed = { song: gone, index: i };
      renderBox();
      syncFile();
      showToast('<span>' + tr('sp_removed', 'Song removed') + '</span><span class="ti-sp-toast__dot">·</span><button type="button" data-ti-sp-undo>' + tr('sp_undo', 'Undo') + '</button>', UNDO_MS);
    }

    function undo() {
      if (!removed) return;
      var songs = st.songs.slice();
      songs.splice(Math.min(removed.index, songs.length), 0, removed.song);
      st.songs = songs.slice(0, MAX_SONGS);
      removed = null;
      hideToast();
      renderBox();
      syncFile();
    }

    function removeAll() {
      fileSeq++;
      st.songs = [];
      st.file = '';
      removed = null;
      hideToast();
      boxError(false);
      applyFields();
      renderBox();
      persist();
    }

    /* -------------------------------------------------------------- the box */
    var TAGS = {
      search: [tr('sp_tag_search', 'Lyrics ready ✓'), ''],
      manual: [tr('sp_tag_manual', 'Your own lyrics ✓'), ''],
      assist: [tr('sp_tag_assist', 'Our team will find the lyrics'), ' ti-sp-tag--assist']
    };

    function songCard(s, i, multi) {
      var tag = TAGS[s.source] || TAGS.search;
      var artist = s.artist || (s.source === 'assist' ? tr('sp_sent_team', 'Sent to our team') : (s.source === 'manual' ? tr('sp_original', 'Original song') : ''));
      return '<div class="ti-sp-chosen' + (multi ? ' ti-sp-chosen--row' : '') + '">' +
        artHtml(s, multi ? 44 : 52, s.tone || i, multi ? i + 1 : 0) +
        '<div class="ti-sp-chosen__body"><div class="ti-sp-chosen__title">' + esc(s.title) + '</div>' +
        '<div class="ti-sp-chosen__artist">' + esc(artist) + '</div>' +
        '<span class="ti-sp-tag' + tag[1] + '">' + tag[0] + '</span></div>' +
        '<button type="button" class="ti-sp-link" data-ti-sp-change="' + i + '">' + tr('sp_change', 'Change') + '</button>' +
        (multi ? '<button type="button" class="ti-sp-remove" data-ti-sp-remove="' + i + '" aria-label="' + tr('sp_remove_n', 'Remove song {n}').replace('{n}', i + 1) + '">&times;</button>' : '') +
      '</div>';
    }

    function renderBox() {
      var songs = st.songs;
      var n = songs.length;
      emptyEl.hidden = n > 0;
      selectedEl.hidden = n === 0;
      if (!n) { selectedEl.innerHTML = ''; return; }
      var h = '';
      if (n === 1) {
        h = songCard(songs[0], 0, false);
      } else {
        h = '<div class="ti-sp-songs-head">' + tr('sp_songs_head', 'Your songs · {n} of {max}').replace('{n}', n).replace('{max}', MAX_SONGS) + '</div>' +
          '<div class="ti-sp-songs">' + songs.map(function (s, i) { return songCard(s, i, true); }).join('') + '</div>' +
          '<div class="ti-sp-info">' + I_INFO + '<span>' + tr('sp_songs_info', 'Your songs are printed one after another, all over the shirt.') + '</span></div>';
      }
      if (n < MAX_SONGS) {
        h += '<button type="button" class="ti-sp-add" data-ti-sp-add>' + I_PLUS + tr('sp_add_another', 'Add another song') + '</button>' +
          '<div class="ti-sp-add-help">' + tr('sp_up_to', 'Up to {max} songs on one shirt').replace('{max}', MAX_SONGS) + '</div>';
      } else {
        h += '<div class="ti-sp-max">' + tr('sp_max', 'That’s the max — {max} songs per item. Remove one to add another.').replace('{max}', MAX_SONGS) + '</div>';
      }
      h += '<div class="ti-sp-box-err" role="alert" hidden>' + tr('sp_box_err', 'We couldn’t save your songs.') + ' <button type="button" class="ti-sp-link" data-ti-sp-retry>' + tr('sp_try_again', 'Try again') + '</button></div>';
      h += '<button type="button" class="ti-sp-link ti-sp-link--muted ti-sp-reset" data-ti-sp-reset>' +
        (n >= 2 ? tr('sp_reset_all', 'Remove all songs & start over') : tr('sp_reset_one', 'Remove song & start over')) + '</button>';
      selectedEl.innerHTML = h;
    }

    function boxError(on) {
      var el = selectedEl.querySelector('.ti-sp-box-err');
      if (el) el.hidden = !on;
    }

    function showToast(html, ms) {
      clearTimeout(toastTimer);
      toast.innerHTML = html;
      toast.hidden = false;
      if (ms) toastTimer = setTimeout(hideToast, ms);
    }

    function hideToast() {
      clearTimeout(toastTimer);
      toast.hidden = true;
      removed = null;
    }

    /* --------------------------------------------------------- open / close */
    function open(view, prefill) {
      if (panel.hidden) lastFocus = document.activeElement;
      panel.hidden = false;
      document.documentElement.classList.add('ti-sp-lock');
      st.prefill = prefill || null;
      st.dupe = -1;
      if (view === 'idle') {
        input.value = '';
        st.query = '';
        st.results = [];
      }
      setView(view);
      setTimeout(function () {
        if (view === 'idle') input.focus({ preventScroll: true });
        else {
          var first = body.querySelector('input, textarea');
          (first || dialog).focus({ preventScroll: true });
        }
      }, 30);
    }

    function close() {
      clearTimeout(debounce);
      searchSeq++;
      lyricsSeq++;
      st.fetching = -1;
      panel.hidden = true;
      document.documentElement.classList.remove('ti-sp-lock');
      if (lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
    }

    function setSlot(i, mode) { st.slot = i; st.mode = mode; }

    function openSearch() { open('idle'); }

    function openAdd() {
      if (st.songs.length >= MAX_SONGS) return;
      setSlot(st.songs.length, 'add');
      openSearch();
    }

    function openManual(fromSong) {
      var s = st.song || {};
      open('manual', fromSong ? { title: s.title, artist: s.artist, lyrics: cleanLyrics(st.lyrics) } : null);
    }

    function openAssist() {
      var s = st.view === 'lyricsError' || st.view === 'confirm' ? st.song : null;
      open('assist', s ? { title: s.title, artist: s.artist } : { title: titleCase(st.query) });
    }

    // "Change" goes back to the step that song was finished on, not to an empty search.
    function review(i) {
      var c = st.songs[i];
      if (!c) return;
      setSlot(i, 'change');
      st.results = [];
      if (c.source === 'search') {
        st.song = { id: c.id, title: c.title, artist: c.artist, image: c.image, release: c.release, url: c.url };
        st.songIndex = c.tone || 0;
        st.lyrics = c.raw || c.lyrics || '';
        st.fetching = -1;
        open('confirm');
      } else if (c.source === 'manual') {
        open('manual', { title: c.title, artist: c.artist, lyrics: c.lyrics });
      } else {
        open('assist', { title: c.title, artist: c.artist, msg: c.note });
      }
    }

    /* ------------------------------------------------- edit a line from the cart */
    var atc = pdp.querySelector('[data-ti-atc]');
    var atcLabel = pdp.querySelector('[data-ti-atc-label]');
    var priceEl = pdp.querySelector('[data-ti-price]');
    var banner = null, editErr = null, savedState = null, priceBefore = '';

    function notice(text) { showToast('<span>' + esc(text) + '</span>', 4000); }

    function stripEditParam() {
      try {
        var u = new URL(location.href);
        u.searchParams.delete('edit');
        history.replaceState(history.state, '', u.pathname + u.search + u.hash);
      } catch (e) { /* ignore */ }
    }

    function songsFromFile(doc) {
      return (doc.songs || []).slice(0, MAX_SONGS).map(function (s, i) {
        var source = FROM_FILE_SOURCE[s.source] || 'manual';
        return {
          source: source, title: s.title || '', artist: s.artist || '', lyrics: s.lyrics || '', raw: s.lyrics || '',
          url: s.genius_url || '', image: s.image || '', release: s.release || '', note: s.note || '', tone: i
        };
      });
    }

    // Lines added before the print file existed carried the lyrics themselves.
    function songsFromProps(p) {
      if (!p['Song Title']) return [];
      if (p._Lyrics) return [{ source: 'manual', title: p['Song Title'], artist: p['Song Artist'] || '', lyrics: p._Lyrics }];
      return [{ source: 'assist', title: p['Song Title'], artist: p['Song Artist'] || '', note: p['Note for our team'] || 'Please use the song from my previous order.' }];
    }

    function selectVariant(item) {
      (item.options_with_values || []).forEach(function (o, gi) {
        var group = pdp.querySelector('[data-ti-option-index="' + gi + '"]');
        if (!group) return;
        var choice = Array.prototype.find.call(group.querySelectorAll('[data-ti-value]'), function (c) { return c.getAttribute('data-ti-value') === o.value; });
        if (choice && !choice.classList.contains('is-active')) choice.click();
      });
      var qty = pdp.querySelector('[data-ti-qty]');
      if (qty) qty.value = item.quantity;
    }

    function startEdit(key) {
      getJSON('/cart.js', 10000).then(function (cart) {
        var item = (cart.items || []).find(function (it) { return it.key === key; });
        if (!item || String(item.product_id) !== String(productId)) {
          stripEditParam();
          notice(tr('sp_gone', 'That item is no longer in your cart'));
          return;
        }
        var props = item.properties || {};
        var load = props['_Lyrics File']
          ? getJSON(props['_Lyrics File'], 10000).then(function (doc) { return { songs: songsFromFile(doc), file: props['_Lyrics File'] }; })
          : Promise.resolve({ songs: songsFromProps(props), file: '' });
        return load.then(function (got) {
          if (!got.songs.length) throw new Error('no songs');
          enterEdit(item, got);
        });
      }).catch(function () {
        stripEditParam();
        notice(tr('sp_open_err', 'We couldn’t open that item. Please try again.'));
      });
    }

    function enterEdit(item, got) {
      fileSeq++;  // a file still being made for the saved songs must not land on this line
      savedState = { songs: st.songs, file: st.file };
      var free = !!(item.properties && item.properties._b2g1_free);
      st.edit = {
        key: item.key, variantId: item.variant_id, props: JSON.stringify(item.properties || {}),
        free: free, freeValue: free ? item.properties._b2g1_free : ''
      };
      st.songs = got.songs;
      st.file = got.file;
      removed = null;

      banner = document.createElement('div');
      banner.className = 'ti-sp-edit';
      banner.innerHTML =
        '<div class="ti-sp-edit__bar" role="status">' + I_PENCIL + '<span>' + tr('sp_editing', 'Editing an item in your cart') + '</span>' +
        '<button type="button" data-ti-sp-edit-cancel>' + tr('sp_cancel', 'Cancel') + '</button></div>' +
        (free ? '<div class="ti-sp-edit__free">' + I_GIFT + '<span>' + tr('sp_free_item', 'This is your free item — it stays free.') + '</span></div>' : '');
      var anchor = pdp.querySelector('.ti-eyebrow') || pdp.querySelector('h1');
      if (anchor) anchor.parentNode.insertBefore(banner, anchor);
      banner.querySelector('[data-ti-sp-edit-cancel]').addEventListener('click', function () { exitEdit(true); });

      if (free) pdp.setAttribute('data-ti-edit-free', '');
      if (atc) {
        atc.setAttribute('data-atc-save', tr('sp_save_changes', 'Save changes'));
        atc.setAttribute('data-atc-save-soldout', tr('sp_choose_size', 'Choose another size'));
      }
      // cart-b2g1.js turns this button into "Add as my FREE item" while a gift is open.
      document.dispatchEvent(new CustomEvent('ti:atc-mode'));
      selectVariant(item);
      if (free && priceEl) { priceBefore = priceEl.textContent; priceEl.textContent = tr('sp_free', 'FREE'); }
      renderBox();
      if (st.file) applyFields(); else syncFile();
      // After the browser's own scroll restore, bring the banner into view.
      setTimeout(function () {
        if (!banner) return;
        var y = banner.getBoundingClientRect().top + window.pageYOffset - 80;
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      }, 350);
    }

    function exitEdit(reopenCart) {
      fileSeq++;
      st.edit = null;
      stripEditParam();
      if (banner) banner.remove();
      banner = null;
      if (editErr) editErr.remove();
      editErr = null;
      pdp.removeAttribute('data-ti-edit-free');
      if (atc) {
        atc.removeAttribute('data-atc-save');
        atc.removeAttribute('data-atc-save-soldout');
      }
      document.dispatchEvent(new CustomEvent('ti:atc-mode'));
      st.songs = savedState ? savedState.songs : [];
      st.file = savedState ? savedState.file : '';
      savedState = null;
      renderBox();
      if (st.songs.length && !st.file) syncFile(); else applyFields();
      // Re-run the section's variant update so the price shows normally again.
      if (priceBefore && priceEl) priceEl.textContent = priceBefore;
      priceBefore = '';
      var active = pdp.querySelector('[data-ti-option-index] [data-ti-value].is-active');
      if (active) active.click();
      if (reopenCart) {
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') drawer.open();
      }
    }

    function editError(on) {
      if (!on) { if (editErr) editErr.remove(); editErr = null; return; }
      if (editErr) return;
      editErr = document.createElement('div');
      editErr.className = 'ti-sp-edit-err';
      editErr.setAttribute('role', 'alert');
      editErr.innerHTML = '<span class="ti-sp-edit-err__i">!</span><span>' + tr('sp_update_err', 'We couldn’t update your cart. Please try again.') + '</span>';
      var row = atc ? atc.closest('.ti-atc-row') || atc : null;
      if (row && row.parentNode) row.parentNode.insertBefore(editErr, row.nextSibling);
    }

    function lineProperties() {
      var props = {};
      new FormData(form).forEach(function (value, name) {
        var m = /^properties\[(.+)\]$/.exec(name);
        if (m && value !== '') props[m[1]] = value;
      });
      if (st.edit.free) props._b2g1_free = st.edit.freeValue || 'true';
      return props;
    }

    function renderDrawer(cart, file) {
      var drawer = document.querySelector('cart-drawer');
      var url = location.pathname + '?sections=cart-drawer,cart-icon-bubble';
      return fetch(url, { cache: 'no-store' }).then(function (r) { return r.json(); }).then(function (sections) {
        if (!drawer || typeof drawer.renderContents !== 'function') { location.href = (window.routes && window.routes.cart_url) || '/cart'; return; }
        drawer.renderContents({ sections: sections });
        // Flash the edited line and say so.
        var idx = (cart.items || []).findIndex(function (it) { return it.properties && it.properties['_Lyrics File'] === file; });
        setTimeout(function () {
          var row = idx >= 0 ? document.getElementById('CartDrawer-Item-' + (idx + 1)) : null;
          if (row) {
            row.classList.add('ti-cart-flash');
            row.scrollIntoView({ block: 'nearest' });
            setTimeout(function () { row.classList.remove('ti-cart-flash'); }, 1500);
          }
          var host = document.getElementById('CartDrawer');
          if (host) {
            var t = document.createElement('div');
            t.className = 'ti-cart-toast';
            t.setAttribute('role', 'status');
            t.textContent = tr('sp_updated', 'Item updated');
            host.appendChild(t);
            setTimeout(function () { t.remove(); }, 3000);
          }
        }, 60);
      });
    }

    function saveEdit() {
      if (!st.edit || !atc || atc.getAttribute('aria-disabled') === 'true' || atc.hasAttribute('data-ti-saving')) return;
      editError(false);
      var e = st.edit;
      var variantId = Number(new FormData(form).get('id'));
      var qtyEl = pdp.querySelector('[data-ti-qty]');
      var qty = Math.max(1, parseInt(qtyEl && qtyEl.value, 10) || 1);
      var props = lineProperties();
      var file = props['_Lyrics File'];

      atc.setAttribute('data-ti-saving', '');
      atc.setAttribute('aria-disabled', 'true');
      var before = atcLabel ? atcLabel.textContent : '';
      if (atcLabel) atcLabel.innerHTML = '<span class="ti-sp-btn-spin"></span>' + tr('sp_saving', 'Saving…');

      // The edited line, found again in a fresh cart: a line's key can change after
      // another write (discounts are re-applied), so match variant + original properties.
      function oldLine() {
        return getJSON('/cart.js', 10000).then(function (cart) {
          var items = cart.items || [];
          var i = items.findIndex(function (it) { return it.key === e.key; });
          if (i < 0) i = items.findIndex(function (it) { return it.variant_id === e.variantId && JSON.stringify(it.properties || {}) === e.props; });
          if (i < 0) throw new Error('line gone');
          return i + 1;
        });
      }

      // Same variant: rewrite the line in place (keeps its place in the cart).
      // Another colour/size: add the new line first, then remove the old one.
      var job = variantId === e.variantId
        ? oldLine().then(function (line) { return cartPost('cart/change.js', { line: line, quantity: qty, properties: props }); })
        : cartPost('cart/add.js', { items: [{ id: variantId, quantity: qty, properties: props }] })
            .then(oldLine)
            .then(function (line) { return cartPost('cart/change.js', { line: line, quantity: 0 }); });

      job.then(function (cart) {
        // A no-op write through fetch lets cart-b2g1.js re-check the free units.
        try { fetch(((window.routes && window.routes.cart_update_url) || '/cart/update') + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }); } catch (x) { /* ignore */ }
        atc.removeAttribute('data-ti-saving');
        exitEdit(false);
        return renderDrawer(cart, file);
      }).catch(function () {
        atc.removeAttribute('data-ti-saving');
        atc.removeAttribute('aria-disabled');
        if (atcLabel) atcLabel.textContent = before;
        editError(true);
      });
    }

    // The product form's own submit handler adds a new line; in edit mode we save instead.
    document.addEventListener('submit', function (ev) {
      if (!st.edit || ev.target !== form) return;
      ev.preventDefault();
      ev.stopImmediatePropagation();
      saveEdit();
    }, true);

    /* ----------------------------------------------------------------- start */
    var editKey = null;
    try { editKey = new URLSearchParams(location.search).get('edit'); } catch (e) { /* ignore */ }

    var saved = loadSaved();
    if (saved) {
      st.songs = saved.songs;
      st.file = saved.file;
      renderBox();
      if (st.file) applyFields(); else syncFile();
    }
    if (editKey) startEdit(editKey);

    /* --------------------------------------------------------------- events */
    box.addEventListener('click', function (e) {
      var t = e.target;
      var b;
      if (t.closest('[data-ti-sp-open]')) { e.preventDefault(); setSlot(0, 'add'); openSearch(); }
      else if (t.closest('[data-ti-sp-manual]')) { e.preventDefault(); setSlot(0, 'add'); openManual(false); }
      else if ((b = t.closest('[data-ti-sp-change]'))) { e.preventDefault(); review(Number(b.getAttribute('data-ti-sp-change')) || 0); }
      else if ((b = t.closest('[data-ti-sp-remove]'))) { e.preventDefault(); removeAt(Number(b.getAttribute('data-ti-sp-remove'))); }
      else if (t.closest('[data-ti-sp-add]')) { e.preventDefault(); openAdd(); }
      else if (t.closest('[data-ti-sp-retry]')) { e.preventDefault(); syncFile(); }
      else if (t.closest('[data-ti-sp-reset]')) { e.preventDefault(); removeAll(); }
    });

    toast.addEventListener('click', function (e) {
      if (e.target.closest('[data-ti-sp-undo]')) undo();
    });

    panel.addEventListener('click', function (e) {
      var b = e.target.closest('[data-act]');
      if (!b) return;
      var act = b.getAttribute('data-act');
      if (act === 'close') close();
      else if (act === 'clear') { input.value = ''; st.query = ''; searchSeq++; setView('idle'); input.focus(); }
      else if (act === 'pick') pick(Number(b.getAttribute('data-i')));
      else if (act === 'search') openSearch();
      else if (act === 'retry') runSearch(input.value);
      else if (act === 'manual') openManual(false);
      else if (act === 'fix') openManual(true);
      else if (act === 'assist') openAssist();
      else if (act === 'back') { st.fetching = -1; st.dupe = -1; setView('results'); }
      else if (act === 'use') {
        var s = st.song;
        commit({ source: 'search', title: s.title, artist: s.artist, lyrics: cleanLyrics(st.lyrics), raw: st.lyrics, id: s.id, url: s.url, image: s.image, release: s.release, tone: st.songIndex }, b);
      }
    });

    input.addEventListener('input', function () {
      clearBtn.hidden = !input.value;
      clearTimeout(debounce);
      var v = input.value;
      debounce = setTimeout(function () { runSearch(v); }, 350);
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); runSearch(input.value); }
    });

    body.addEventListener('input', function (e) {
      updateCounters();
      if (e.target.classList.contains('is-bad')) e.target.classList.remove('is-bad');
    });

    body.addEventListener('submit', function (e) {
      var f = e.target.closest('form[data-form]');
      if (!f) return;
      e.preventDefault();
      var kind = f.getAttribute('data-form');
      var title = f.elements.title.value.trim();
      var artist = f.elements.artist.value.trim();
      var titleErr = f.querySelector('[data-err="title"]');
      var ok = true;
      f.elements.title.classList.toggle('is-bad', !title);
      titleErr.hidden = !!title;
      if (!title) ok = false;
      if (kind === 'manual') {
        var lyrics = f.elements.lyrics.value;
        var short = wordCount(lyrics) < MIN_WORDS;
        f.elements.lyrics.classList.toggle('is-bad', short);
        f.querySelector('[data-err="lyrics"]').textContent = short ? tr('sp_err_short', 'Add a bit more text so we can fill the shirt.') : '';
        if (short) ok = false;
        if (ok) commit({ source: 'manual', title: title, artist: artist, lyrics: lyrics.replace(/\r\n?/g, '\n').trim() }, f.querySelector('[type=submit]'));
      } else {
        var msg = f.elements.msg.value.trim();
        var bad = msg.length < 10;
        f.elements.msg.classList.toggle('is-bad', bad);
        f.querySelector('[data-err="msg"]').textContent = bad ? tr('sp_err_msg', 'Tell us a little more so we can find the right song.') : '';
        if (bad) ok = false;
        if (ok) commit({ source: 'assist', title: title, artist: artist, note: msg.slice(0, NOTE_MAX) }, f.querySelector('[type=submit]'));
      }
      if (!ok) {
        var firstBad = f.querySelector('.is-bad');
        if (firstBad) firstBad.focus();
      }
    });

    // Esc closes; Tab stays inside the dialog.
    panel.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key !== 'Tab') return;
      var els = Array.prototype.filter.call(
        dialog.querySelectorAll('button, input, textarea, [tabindex="0"]'),
        function (el) { return !el.disabled && el.offsetParent !== null; }
      );
      if (!els.length) return;
      var first = els[0], last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  function boot() { document.querySelectorAll('[data-ti-sp]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
